import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { AutoAwesomeOutlinedIcon, OpenInNewIcon } from "icons";
import { colors } from "theme/palette";

import Button from "components/Button";
import Collapse from "components/Collapse";
import Divider from "components/Divider";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useAIMemoSection } from "components/useAIMemoSection";

import StatusTag from "entities/Memo/components/StatusTag";
import {
  IMemo,
  ISourceDocument,
  memoSectionAIProcessTrigger,
} from "entities/Memo/sdk";
import { EMemoSectionType } from "entities/MemoTemplate/constants";

import { handleInvalidMemoFieldRequest } from "../Fields/utils/utils";

const SourceDocumentCollapse = ({
  open,
  memo,
  hasMemoAISections,
  onUpdate,
  disableGenerateButtonOnClick,
  sourceDocuments,
  disableGenerateButtonOnClickHandler,
}: {
  open: boolean;
  memo: IMemo;
  hasMemoAISections: boolean;
  onUpdate: () => void;
  disableGenerateButtonOnClick: boolean;
  sourceDocuments?: Array<ISourceDocument>;
  disableGenerateButtonOnClickHandler: ({
    disable,
  }: {
    disable: boolean;
  }) => void;
}) => {
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const { handleSelectedSourceDocumentId } = useAIMemoSection();

  const memoSectionAIAnswerIds = useMemo(() => {
    if (_.isNil(memo) || !hasMemoAISections) return [];

    return memo.sections
      .filter((section) => {
        const isAIAnswer = section.section_type === EMemoSectionType.AI_ANSWER;

        const isMissingRichValue =
          _.isEmpty(_.first(section.fields)?.values) ||
          EMPTY_VALUE_REGEX.test(
            _.first(section.fields)?.values[0]?.rich_value || ""
          );

        const allDocumentsValid = section.ai_prompt?.source_documents?.every(
          (document) => !document.is_missing && !document.is_na
        );

        const isAnswerBeingGenerated = !section.is_prompt_response_in_progress;

        const isSectionNA = section.is_na;

        return (
          isAIAnswer &&
          isMissingRichValue &&
          allDocumentsValid &&
          isAnswerBeingGenerated &&
          !isSectionNA
        );
      })
      .map((section) => section.section_id);
  }, [memo, hasMemoAISections]);

  const disableGeneratePromptsForAllSections = useMemo(
    () => memoSectionAIAnswerIds.length === 0 || disableGenerateButtonOnClick,
    [memoSectionAIAnswerIds.length, disableGenerateButtonOnClick]
  );

  const handleMemoSectionAIExtractionTrigger = useCallback(() => {
    disableGenerateButtonOnClickHandler({ disable: true });
    memoSectionAIProcessTrigger({
      memoId,
      dealId,
      memoSectionIds: memoSectionAIAnswerIds,
    })
      .then(() => {
        onUpdate();
        toast.successMessage(
          "The AI Extraction has started, you will be notified when that data has been processed!"
        );
      })
      .catch(handleInvalidMemoFieldRequest);
  }, [
    memoId,
    dealId,
    memoSectionAIAnswerIds,
    onUpdate,
    disableGenerateButtonOnClickHandler,
  ]);

  return (
    <Collapse in={open} timeout="auto">
      <Stack gap={1} alignItems="flex-start">
        <Stack gap={1} width="100%" marginTop={1}>
          {sourceDocuments?.map((document, index) => (
            <>
              <Stack
                direction="row"
                key={document.id}
                justifyContent="space-between"
              >
                <Stack direction="row" gap={1} key={document.id}>
                  <Text
                    fontSize="16px"
                    fontWeight={700}
                    padding={0.8}
                    borderRadius={1}
                    onClick={() => {
                      handleSelectedSourceDocumentId({
                        value: document.id,
                      });
                    }}
                    sx={{
                      cursor: "pointer",
                      "&:hover ": {
                        backgroundColor: colors.blue10,
                      },
                    }}
                  >
                    {index + 1}. {document.name}
                  </Text>
                  {document.is_missing && !document.is_na && (
                    <StatusTag text="missing" backgroundColor={colors.red40} />
                  )}
                  {document.is_na && (
                    <StatusTag text="N/A" backgroundColor={colors.gray20} />
                  )}
                </Stack>
                <Stack
                  alignSelf="center"
                  width="30px"
                  height="30px"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    marginRight: 1,
                    cursor: "pointer",
                    "&:hover ": {
                      backgroundColor: colors.blue10,
                    },
                  }}
                >
                  <OpenInNewIcon
                    sx={{
                      color: colors.gray60,
                    }}
                    onClick={() => {
                      handleSelectedSourceDocumentId({
                        value: document.id,
                      });
                    }}
                  />
                </Stack>
              </Stack>
              <Divider
                sx={{
                  color: colors.blue80,
                  width: "100%",
                }}
              />
            </>
          ))}
          <Tooltip
            title={
              memoSectionAIAnswerIds.length === 0
                ? "There are no sections available for AI summarization!"
                : undefined
            }
          >
            <span
              style={{
                width: "fit-content",
                cursor: disableGeneratePromptsForAllSections
                  ? "not-allowed"
                  : "pointer",
              }}
            >
              <Button
                color="secondary"
                sx={{
                  transition: "opacity 0.2s ease-in-out",
                  opacity: disableGeneratePromptsForAllSections ? 0.5 : 1,
                  padding: 1,
                }}
                onClick={handleMemoSectionAIExtractionTrigger}
                disabled={disableGeneratePromptsForAllSections}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  color={
                    disableGeneratePromptsForAllSections
                      ? colors.gray80
                      : colors.blue100
                  }
                >
                  <AutoAwesomeOutlinedIcon
                    sx={{
                      width: "16px",
                      height: "16px",
                      fontWeight: 500,
                      transition: "color 0.2s ease-in-out",
                    }}
                  />
                  <Text
                    sx={{
                      fontWeight: 500,
                      transition: "color 0.2s ease-in-out",
                    }}
                  >
                    Generate for all empty sections
                  </Text>
                </Stack>
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Collapse>
  );
};

export default SourceDocumentCollapse;
